export function normalizeText(value) {
  if (value === null || value === undefined) return "";
  const text = String(value).toLowerCase();
  try {
    return text.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  } catch (error) {
    return text;
  }
}

export function parseDateValue(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  const raw = String(value);
  const normalized = /^\d{4}-\d{2}$/.test(raw) ? `${raw}-01` : raw;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function dateInputValue(value) {
  const date = parseDateValue(value);
  if (!date) return null;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

export function periodDateRange(period) {
  if (!period || period === "all" || period === "custom") return null;

  const now = new Date();
  let start = null;

  if (period === "current_month") {
    start = new Date(now.getFullYear(), now.getMonth(), 1);
  } else if (period === "last_3_months") {
    start = new Date(now.getFullYear(), now.getMonth() - 2, 1);
  } else if (period === "last_6_months") {
    start = new Date(now.getFullYear(), now.getMonth() - 5, 1);
  } else if (period === "current_year") {
    start = new Date(now.getFullYear(), 0, 1);
  }

  if (!start) return null;
  return {
    start: dateInputValue(start),
    end: dateInputValue(now),
  };
}

export function resolveDateRangeFilters(filters = {}) {
  const periodRange = periodDateRange(filters.period);
  if (periodRange) return periodRange;
  if (!filters.period || filters.period === "custom") {
    return {
      start: filters.start_date || "",
      end: filters.end_date || "",
    };
  }
  return { start: "", end: "" };
}

function shallowValues(item) {
  return Object.keys(item || {}).reduce((values, key) => {
    const value = item[key];
    if (value === null || value === undefined) return values;
    if (["string", "number", "boolean"].includes(typeof value)) values.push(value);
    return values;
  }, []);
}

function valuesFromKeys(item, keys) {
  if (!keys || !keys.length) return shallowValues(item);
  return keys.map((key) => item && item[key]).filter((value) => value !== null && value !== undefined);
}

function matchesSearch(item, search, keys) {
  const needle = normalizeText(search);
  if (!needle) return true;
  return valuesFromKeys(item, keys).some((value) => normalizeText(value).includes(needle));
}

function matchesDateRange(item, filters, keys) {
  const range = resolveDateRangeFilters(filters);
  const start = parseDateValue(range.start);
  const end = parseDateValue(range.end);
  if (!start && !end) return true;

  const dates = valuesFromKeys(item, keys).map(parseDateValue).filter(Boolean);
  if (!dates.length) return false;

  if (end) end.setHours(23, 59, 59, 999);
  return dates.some((date) => {
    if (start && date < start) return false;
    if (end && date > end) return false;
    return true;
  });
}

function matchesField(item, key, value, matcher) {
  if (value === "" || value === null || value === undefined) return true;
  if (Array.isArray(value) && !value.length) return true;
  if (matcher) return matcher(item, value);

  const current = item && item[key];
  if (Array.isArray(value)) return value.map(String).includes(String(current));
  return normalizeText(current) === normalizeText(value);
}

export function applyListFilters(items, filters = {}, options = {}) {
  const textKeys = options.textKeys || [];
  const dateKeys = options.dateKeys || ["created_at", "updated_at", "due_date", "expires_at", "starts_at", "ends_at", "received_at", "reference_month"];
  const fieldMatchers = options.fieldMatchers || {};
  const ignoredKeys = ["search", "period", "start_date", "end_date"];

  return (items || []).filter((item) => {
    if (!matchesSearch(item, filters.search, textKeys)) return false;
    if (!matchesDateRange(item, filters, dateKeys)) return false;

    return Object.keys(filters).every((key) => {
      if (ignoredKeys.includes(key)) return true;
      return matchesField(item, key, filters[key], fieldMatchers[key]);
    });
  });
}

export function initialListFilters(extra = {}) {
  return Object.assign({ search: "", period: "all", start_date: "", end_date: "" }, extra);
}
